package sarka.design.skoria.navigation

import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.lifecycle.compose.LifecycleResumeEffect
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import sarka.design.skoria.messaging.MessageRepository
import sarka.design.skoria.network.TokenStore
import sarka.design.skoria.presentation.ChatController
import sarka.design.skoria.presentation.ConversationListViewModel
import sarka.design.skoria.presentation.ConversationViewModel
import sarka.design.skoria.presentation.HomeViewModel
import sarka.design.skoria.presentation.LoginViewModel
import sarka.design.skoria.presentation.RegisterViewModel
import sarka.design.skoria.presentation.VerifyEmailViewModel
import sarka.design.skoria.ui.ChatScreen
import sarka.design.skoria.ui.ConversationListScreen
import sarka.design.skoria.ui.ConversationScreen
import sarka.design.skoria.ui.HomeScreen
import sarka.design.skoria.ui.LoginScreen
import sarka.design.skoria.ui.RegisterScreen
import sarka.design.skoria.ui.VerifyEmailScreen

@Composable
fun SkoriaNavHost(navController: NavHostController) {
    // One repository for both messaging screens, so they share a single /dm
    // socket instead of opening one each.
    val messageRepository = remember { MessageRepository() }

    DisposableEffect(Unit) {
        onDispose { messageRepository.dispose() }
    }

    // Close the DM socket the moment the session ends. Without this it keeps
    // looping on reconnect after logout, and would attach to whoever logs in
    // next on the same device.
    val sessionToken by TokenStore.token.collectAsState()
    LaunchedEffect(sessionToken) {
        if (sessionToken == null) messageRepository.stopListening()
    }

    // Restore persisted session on first composition
    LaunchedEffect(Unit) {
        TokenStore.load()
        if (TokenStore.isLoggedIn) {
            navController.navigate(Routes.HOME) {
                popUpTo(Routes.LOGIN) { inclusive = true }
            }
        }
    }

    NavHost(
        navController = navController,
        startDestination = Routes.LOGIN,
        enterTransition = { EnterTransition.None },
        exitTransition = { ExitTransition.None },
        popEnterTransition = { EnterTransition.None },
        popExitTransition = { ExitTransition.None },
    ) {
        composable(Routes.LOGIN) {
            val viewModel: LoginViewModel = viewModel { LoginViewModel() }
            val state by viewModel.state.collectAsState()

            LoginScreen(
                state = state,
                onUsernameChange = viewModel::onUsernameChange,
                onPasswordChange = viewModel::onPasswordChange,
                onLogin = viewModel::login,
                onLoginSuccess = {
                    navController.navigate(Routes.HOME) {
                        popUpTo(Routes.LOGIN) { inclusive = true }
                    }
                },
                onLoginClick = {
                    navController.navigate(Routes.HOME) {
                        launchSingleTop = true
                    }
                },
                onRegisterClick = {
                    navController.navigate(Routes.REGISTER)
                },
                onVerifyEmailClick = {
                    // Email is unknown here; VerifyEmailViewModel fetches the
                    // masked address when it's blank.
                    NavArgs.verifyEmailUsername = state.username
                    NavArgs.verifyEmailEmail = ""
                    navController.navigate(Routes.VERIFY_EMAIL)
                }
            )
        }

        composable(Routes.REGISTER) {
            val viewModel: RegisterViewModel = viewModel { RegisterViewModel() }
            val state by viewModel.state.collectAsState()

            RegisterScreen(
                state = state,
                onUsernameChange = viewModel::onUsernameChange,
                onEmailChange = viewModel::onEmailChange,
                onPasswordChange = viewModel::onPasswordChange,
                onConfirmPasswordChange = viewModel::onConfirmPasswordChange,
                onRegister = viewModel::register,
                onRegistrationSuccess = { username, email ->
                    NavArgs.verifyEmailUsername = username
                    NavArgs.verifyEmailEmail = email
                    navController.navigate(Routes.VERIFY_EMAIL) {
                        popUpTo(Routes.REGISTER) { inclusive = true }
                    }
                },
                onBackToLogin = {
                    navController.popBackStack()
                }
            )
        }

        composable(Routes.VERIFY_EMAIL) {
            // Snapshot the parameters as the screen is created; NavArgs may be
            // overwritten by a later navigation.
            val username = remember { NavArgs.verifyEmailUsername.orEmpty() }
            val email = remember { NavArgs.verifyEmailEmail }

            val viewModel: VerifyEmailViewModel = viewModel {
                VerifyEmailViewModel(username, email)
            }
            val state by viewModel.state.collectAsState()

            VerifyEmailScreen(
                state = state,
                onCodeChange = viewModel::onCodeChange,
                onVerify = viewModel::verifyEmail,
                onResendCode = viewModel::resendCode,
                onVerificationSuccess = {
                    navController.navigate(Routes.HOME) {
                        popUpTo(Routes.LOGIN) { inclusive = true }
                    }
                }
            )
        }

        composable(Routes.HOME) {
            val homeViewModel: HomeViewModel = viewModel { HomeViewModel() }
            val loggedOut by homeViewModel.loggedOut.collectAsState()
            val username by homeViewModel.username.collectAsState()

            LaunchedEffect(loggedOut) {
                if (loggedOut) {
                    navController.navigate(Routes.LOGIN) {
                        popUpTo(Routes.HOME) { inclusive = true }
                    }
                }
            }

            HomeScreen(
                username = username,
                isLoggedIn = sessionToken != null,
                onVideoChatClick = {
                    navController.navigate(Routes.CHAT) {
                        launchSingleTop = true
                    }
                },
                onTextChatClick = {
                    navController.navigate(Routes.MESSAGES) {
                        launchSingleTop = true
                    }
                },
                onLogout = homeViewModel::logout
            )
        }

        composable(Routes.MESSAGES) {
            val viewModel: ConversationListViewModel = viewModel {
                ConversationListViewModel(messageRepository)
            }
            val state by viewModel.state.collectAsState()

            // Refresh whenever this screen comes back to the foreground, e.g.
            // returning from a conversation after reading it. Without this the
            // unread badge would stay stale until the screen is recreated.
            LifecycleResumeEffect(Unit) {
                viewModel.refresh()
                onPauseOrDispose { }
            }

            ConversationListScreen(
                state = state,
                onAddFriendUsernameChange = viewModel::onAddFriendUsernameChange,
                onSendFriendRequest = viewModel::sendFriendRequest,
                onAcceptRequest = viewModel::acceptRequest,
                onDeclineRequest = viewModel::declineRequest,
                onCancelRequest = viewModel::cancelRequest,
                onOpenConversation = { username ->
                    NavArgs.conversationWith = username
                    navController.navigate(Routes.CONVERSATION)
                },
                onBack = { navController.popBackStack() },
            )
        }

        composable(Routes.CONVERSATION) {
            // Snapshot the target as the screen is created; NavArgs may change
            // if the user opens another conversation later.
            val withUser = remember { NavArgs.conversationWith.orEmpty() }

            // Keyed on the username so switching conversations gets a fresh
            // ViewModel rather than reusing the previous one's messages.
            val viewModel: ConversationViewModel = viewModel(key = "conversation_$withUser") {
                ConversationViewModel(withUser, messageRepository)
            }
            val state by viewModel.state.collectAsState()

            ConversationScreen(
                state = state,
                onInputChange = viewModel::onInputChange,
                onSend = viewModel::send,
                onLoadOlder = viewModel::loadOlder,
                onBack = { navController.popBackStack() },
            )
        }

        composable(Routes.CHAT) {
            val scope = rememberCoroutineScope()
            val controller = remember {
                ChatController(scope = scope)
            }

            LaunchedEffect(Unit) {
                controller.connect()
            }

            // Cleanup when leaving chat screen
            DisposableEffect(Unit) {
                onDispose {
                    controller.dispose()
                }
            }

            val state by controller.state.collectAsState()

            ChatScreen(
                controller = controller,
                state = state,
                onInputChange = controller::onInputChange,
                onSend = controller::send,
                onSkip = controller::skip,
                onBack = { navController.popBackStack() },
                onReconnect = controller::connect,
            )
        }
    }
}
